import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./hooks/useAuth";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Componentes de autenticação (carregados imediatamente)
import LoginPage from "./components/Auth/LoginPage";
import RegisterPage from "./components/Auth/RegisterPage";
import RegisterSuccessPage from "./components/Auth/RegisterSuccessPage";
import EsqueciSenhaPage from "./components/Auth/EsqueciSenhaPage";

// Lazy loading para componentes autenticados (carregados sob demanda)
// Proponente
const HomeProponente = lazy(() => import("./components/Proponente/Home/HomeProponente"));
const CompletarCadastroWizard = lazy(() => import("./components/Proponente/CompletarCadastro/CompletarCadastroWizard"));
const ListaEditaisPublicos = lazy(() => import("./components/Proponente/Editais").then(m => ({ default: m.ListaEditaisPublicos })));
const DetalhesEditalPublico = lazy(() => import("./components/Proponente/Editais").then(m => ({ default: m.DetalhesEditalPublico })));
const MinhasInscricoes = lazy(() => import("./components/Proponente/Inscricoes").then(m => ({ default: m.MinhasInscricoes })));
const FormInscricao = lazy(() => import("./components/Proponente/Inscricoes").then(m => ({ default: m.FormInscricao })));
const DetalhesInscricaoProponente = lazy(() => import("./components/Proponente/Inscricoes").then(m => ({ default: m.DetalhesInscricao })));
const MinhasAreasExpertise = lazy(() => import("./pages/Proponente/MinhasAreasExpertise").then(m => ({ default: m.MinhasAreasExpertise })));

// Admin
const HomeAdmin = lazy(() => import("./components/Admin/Home/Home"));
const Proponentes = lazy(() => import("./components/Admin/Proponentes/Proponentes"));
const ProponenteDetail = lazy(() => import("./components/Admin/Proponentes/ProponenteDetail"));
const AdminAvaliadores = lazy(() => import("./pages/Admin/Avaliadores/AdminAvaliadores").then(m => ({ default: m.AdminAvaliadores })));
const ListaInscricoes = lazy(() => import("./components/Admin/Inscricoes").then(m => ({ default: m.ListaInscricoes })));
const DetalhesInscricao = lazy(() => import("./components/Admin/Inscricoes").then(m => ({ default: m.DetalhesInscricao })));
const TriagemInscricao = lazy(() => import("./components/Admin/Inscricoes/TriagemInscricao").then(m => ({ default: m.TriagemInscricao })));
const ListaEditais = lazy(() => import("./components/Admin/Editais").then(m => ({ default: m.ListaEditais })));
const FormEdital = lazy(() => import("./components/Admin/Editais").then(m => ({ default: m.FormEdital })));
const DetalhesEdital = lazy(() => import("./components/Admin/Editais").then(m => ({ default: m.DetalhesEdital })));
const ListaTemplates = lazy(() => import("./components/Admin/Templates").then(m => ({ default: m.ListaTemplates })));
const FormTemplate = lazy(() => import("./components/Admin/Templates").then(m => ({ default: m.FormTemplate })));
const DetalhesTemplate = lazy(() => import("./components/Admin/Templates").then(m => ({ default: m.DetalhesTemplate })));
const GestaoCriterios = lazy(() => import("./components/Admin/Criterios/GestaoCriterios"));
const ConviteAvaliadores = lazy(() => import("./components/Admin/Avaliacoes/ConviteAvaliadores"));
const DashboardAvaliacoes = lazy(() => import("./pages/Admin/Avaliacoes/DashboardAvaliacoes"));
const MonitorarAvaliacoes = lazy(() => import("./pages/Admin/Avaliacoes/MonitorarAvaliacoes"));

// Avaliador
const MeusConvites = lazy(() => import("./pages/Avaliador/MeusConvites"));
const MinhasAvaliacoes = lazy(() => import("./pages/Avaliador/MinhasAvaliacoes"));
const AvaliarProjeto = lazy(() => import("./pages/Avaliador/AvaliarProjeto"));

// Componente de loading para lazy imports
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
      <p className="text-gray-600">Carregando...</p>
    </div>
  </div>
);

// Componente de rotas que usa o contexto de auth
function AppRoutes() {
  const { user, isAuthenticated, loading } = useAuth();

  // Enquanto está verificando autenticação, faz verificação silenciosa
  if (loading) {
    return null;
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
      {/* Rota inicial com redirecionamento inteligente */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            user?.tipo_usuario === "proponente" &&
            user.has_proponente === false ? (
              <Navigate to="/completar-cadastro" replace />
            ) : (
              <Navigate
                to={user?.tipo_usuario === "admin" ? "/admin/home" : "/home"}
                replace
              />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Rotas públicas (sem autenticação) */}
      <Route
        path="/login"
        element={
          isAuthenticated ? (
            user?.tipo_usuario === "proponente" &&
            user.has_proponente === false ? (
              <Navigate to="/completar-cadastro" replace />
            ) : (
              <Navigate
                to={user?.tipo_usuario === "admin" ? "/admin/home" : "/home"}
                replace
              />
            )
          ) : (
            <LoginPage />
          )
        }
      />
      <Route
        path="/cadastro"
        element={
          isAuthenticated ? (
            <Navigate
              to={user?.tipo_usuario === "admin" ? "/admin/home" : "/home"}
              replace
            />
          ) : (
            <RegisterPage />
          )
        }
      />
      <Route path="/register/success" element={<RegisterSuccessPage />} />
      <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />

      {/* Rotas protegidas - Usuário */}
      <Route
        path="/home"
        element={
          <ProtectedRoute requiredRole="proponente">
            <HomeProponente />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Proponente (editais) */}
      <Route
        path="/editais"
        element={
          <ProtectedRoute requiredRole="proponente">
            <ListaEditaisPublicos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/editais/:id"
        element={
          <ProtectedRoute requiredRole="proponente">
            <DetalhesEditalPublico />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Proponente (inscrições) */}
      <Route
        path="/inscricoes"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MinhasInscricoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inscricoes/nova"
        element={
          <ProtectedRoute requiredRole="proponente">
            <FormInscricao />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inscricoes/:id"
        element={
          <ProtectedRoute requiredRole="proponente">
            <DetalhesInscricaoProponente />
          </ProtectedRoute>
        }
      />
      <Route
        path="/areas-expertise"
        element={
          <ProtectedRoute requiredRole="proponente">
            <MinhasAreasExpertise />
          </ProtectedRoute>
        }
      />

      {/* Rota para completar cadastro - Proponente */}
      <Route
        path="/completar-cadastro"
        element={
          <ProtectedRoute requiredRole="proponente">
            <CompletarCadastroWizard />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (proponentes) */}
      <Route
        path="/admin/proponentes"
        element={
          <ProtectedRoute requiredRole="admin">
            <Proponentes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/proponentes/:id"
        element={
          <ProtectedRoute requiredRole="admin">
            <ProponenteDetail />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (avaliadores) */}
      <Route
        path="/admin/avaliadores"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminAvaliadores />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin */}
      <Route
        path="/admin/home"
        element={
          <ProtectedRoute requiredRole="admin">
            <HomeAdmin />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (templates) */}
      <Route
        path="/admin/templates"
        element={
          <ProtectedRoute requiredRole="admin">
            <ListaTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates/novo"
        element={
          <ProtectedRoute requiredRole="admin">
            <FormTemplate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates/:id"
        element={
          <ProtectedRoute requiredRole="admin">
            <DetalhesTemplate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/templates/:id/editar"
        element={
          <ProtectedRoute requiredRole="admin">
            <FormTemplate />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (editais) */}
      <Route
        path="/admin/editais"
        element={
          <ProtectedRoute requiredRole="admin">
            <ListaEditais />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/novo"
        element={
          <ProtectedRoute requiredRole="admin">
            <FormEdital />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/:id"
        element={
          <ProtectedRoute requiredRole="admin">
            <DetalhesEdital />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/editais/:id/editar"
        element={
          <ProtectedRoute requiredRole="admin">
            <FormEdital />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (inscrições) */}
      <Route
        path="/admin/inscricoes"
        element={
          <ProtectedRoute requiredRole="admin">
            <ListaInscricoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inscricoes/:id"
        element={
          <ProtectedRoute requiredRole="admin">
            <DetalhesInscricao />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inscricoes/:id/triagem"
        element={
          <ProtectedRoute requiredRole="admin">
            <TriagemInscricao />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (critérios de avaliação) */}
      <Route
        path="/admin/editais/:id/criterios"
        element={
          <ProtectedRoute requiredRole="admin">
            <GestaoCriterios />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Admin (avaliações) */}
      <Route
        path="/admin/inscricoes/:id/avaliacoes"
        element={
          <ProtectedRoute requiredRole="admin">
            <ConviteAvaliadores />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/avaliacoes/dashboard"
        element={
          <ProtectedRoute requiredRole="admin">
            <DashboardAvaliacoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projetos/:id/avaliacoes"
        element={
          <ProtectedRoute requiredRole="admin">
            <MonitorarAvaliacoes />
          </ProtectedRoute>
        }
      />

      {/* Rotas protegidas - Avaliador */}
      <Route
        path="/avaliador/convites"
        element={
          <ProtectedRoute>
            <MeusConvites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avaliador/avaliacoes"
        element={
          <ProtectedRoute>
            <MinhasAvaliacoes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/avaliador/avaliar/:id"
        element={
          <ProtectedRoute>
            <AvaliarProjeto />
          </ProtectedRoute>
        }
      />

      {/* Rota 404 - redireciona baseado na autenticação */}
      <Route
        path="*"
        element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />}
      />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
